import React, { Suspense, useEffect } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { api } from "@/lib/api";
import "@/App.css";
import "@/lib/audioUnlock";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import { ProfileHoverCardHost } from "./components/ProfileHoverCard";
import { CookieConsent } from "./components/cookies/CookieConsent";
import { ModalA11y } from "./components/a11y/ModalA11y";
import { PrivacySync } from "./components/a11y/PrivacySync";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SocketProvider } from "@/context/SocketContext";
import { CallProvider } from "@/context/CallContext";
import { MetaProvider } from "@/context/MetaContext";
import { ConfirmProvider } from "@/context/ConfirmContext";
import { Layout } from "@/components/Layout";
import { OfflineBanner } from "@/components/OfflineBanner";
import { RulesGate } from "@/components/RulesGate";
import { CinemaPlayerProvider } from "@/context/CinemaPlayerContext";
import { SafetyAlertProvider } from "@/context/SafetyAlertContext";

const Auth = lazy(() => import("@/pages/Auth"));
const Feed = lazy(() => import("@/pages/Feed"));
const Messages = lazy(() => import("@/pages/Messages"));
const Rooms = lazy(() => import("@/pages/Rooms"));
const PackMates = lazy(() => import("@/pages/PackMates"));
const Profile = lazy(() => import("@/pages/Profile"));
const Admin = lazy(() => import("@/pages/Admin"));
const MysticProfile = lazy(() => import("@/pages/MysticProfile"));
const Quests = lazy(() => import("@/pages/Quests"));
const Shop = lazy(() => import("@/pages/Shop"));
const Events = lazy(() => import("@/pages/Events"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const Achievements = lazy(() => import("@/pages/Achievements"));
const Recruiters = lazy(() => import("@/pages/Recruiters"));
const Academy = lazy(() => import("@/pages/Academy"));
const AcademyCategory = lazy(() => import("@/pages/AcademyCategory"));
const PackCode = lazy(() => import("@/pages/PackCode"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Helpdesk = lazy(() => import("@/pages/Helpdesk"));
const HowlPage = lazy(() => import("@/pages/HowlPage"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const Cinema = lazy(() => import("@/pages/Cinema"));
const PackMap = lazy(() => import("@/pages/PackMap"));
const CinemaPerformer = lazy(() => import("@/pages/CinemaPerformer"));
const WatchParty = lazy(() => import("@/pages/WatchParty"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const PrivacyNotice = lazy(() => import("@/pages/PrivacyNotice"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const TermsOfUse = lazy(() => import("@/pages/TermsOfUse"));
const Security = lazy(() => import("@/pages/Security"));
const Blocked = lazy(() => import("@/pages/Blocked"));
const DisplaySettings = lazy(() => import("@/pages/DisplaySettings"));
const AccessibilityPage = lazy(() => import("@/pages/Accessibility"));
const AccessibilityStatement = lazy(() => import("@/pages/AccessibilityStatement"));
const CcpaOptOut = lazy(() => import("@/pages/CcpaOptOut"));
const CompliancePage = lazy(() => import("@/pages/CompliancePage"));
const Merch = lazy(() => import("@/pages/Merch"));
const SafetyAlert = lazy(() => import("@/pages/SafetyAlert"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Onboarding = lazy(() => import("@/components/Onboarding").then((m) => ({ default: m.Onboarding })));

function PageLoader() {
  return (
    <div className="min-h-screen sanctuary-bg flex items-center justify-center">
      <Loader2 className="w-10 h-10 animate-spin text-sky-500" />
    </div>
  );
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <PageLoader />;
  if (!user) {
    if (location.pathname !== "/") sessionStorage.setItem("returnTo", location.pathname + location.search);
    return <Navigate to="/enter" replace />;
  }
  const returnTo = sessionStorage.getItem("returnTo");
  if (returnTo) {
    if (returnTo !== location.pathname + location.search) return <Navigate to={returnTo} replace />;
    sessionStorage.removeItem("returnTo");
  }
  return (
    <SocketProvider>
      <MetaProvider>
        <ConfirmProvider>
          <CallProvider>
            <OfflineBanner />
            {user.onboarded === false && <Suspense fallback={null}><Onboarding /></Suspense>}
            <RulesGate />
            <CinemaPlayerProvider>
              <SafetyAlertProvider>
                <Layout><ErrorBoundary key={location.pathname} label="page">{children}</ErrorBoundary></Layout>
              </SafetyAlertProvider>
            </CinemaPlayerProvider>
          </CallProvider>
        </ConfirmProvider>
      </MetaProvider>
    </SocketProvider>
  );
}

function Preloader() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user) return undefined;
    const run = () => {
      import("@/pages/Rooms"); import("@/pages/Academy"); import("@/pages/Feed"); import("@/pages/Messages");
      api.get("/rooms").catch(() => {}); api.get("/academy/overview").catch(() => {});
    };
    const id = window.requestIdleCallback ? window.requestIdleCallback(run, { timeout: 4000 }) : setTimeout(run, 2500);
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function AuthGate() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/" replace />;
  return <Auth />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ProfileHoverCardHost />
          <Preloader />
          <CookieConsent />
          <ModalA11y />
          <PrivacySync />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/enter" element={<AuthGate />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/privacy-policy" element={<PrivacyNotice />} />
              <Route path="/privacy" element={<PrivacyNotice />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/terms" element={<TermsOfUse />} />
              <Route path="/" element={<Protected><Feed /></Protected>} />
              <Route path="/messages" element={<Protected><Messages /></Protected>} />
              <Route path="/dens" element={<Protected><Rooms /></Protected>} />
              <Route path="/mates" element={<Protected><PackMates /></Protected>} />
              <Route path="/quests" element={<Protected><Quests /></Protected>} />
              <Route path="/shop" element={<Protected><Shop /></Protected>} />
              <Route path="/merch" element={<Protected><Merch /></Protected>} />
              <Route path="/events" element={<Protected><Events /></Protected>} />
              <Route path="/signals" element={<Protected><Notifications /></Protected>} />
              <Route path="/achievements" element={<Protected><Achievements /></Protected>} />
              <Route path="/recruiters" element={<Protected><Recruiters /></Protected>} />
              <Route path="/academy" element={<Protected><Academy /></Protected>} />
              <Route path="/academy/category/:name" element={<Protected><AcademyCategory /></Protected>} />
              <Route path="/rules" element={<Protected><PackCode /></Protected>} />
              <Route path="/blog" element={<Protected><Blog /></Protected>} />
              <Route path="/blog/:id" element={<Protected><BlogPost /></Protected>} />
              <Route path="/helpdesk" element={<Protected><Helpdesk /></Protected>} />
              <Route path="/feedback" element={<Navigate to="/helpdesk" replace />} />
              <Route path="/cinema" element={<Protected><Cinema /></Protected>} />
              <Route path="/map" element={<Protected><PackMap /></Protected>} />
              <Route path="/cinema/performer/:name" element={<Protected><CinemaPerformer /></Protected>} />
              <Route path="/cinema/party/:id" element={<Protected><WatchParty /></Protected>} />
              <Route path="/howl/:id" element={<Protected><HowlPage /></Protected>} />
              <Route path="/profile" element={<Protected><Profile /></Protected>} />
              <Route path="/privacy-settings" element={<Protected><Privacy /></Protected>} />
              <Route path="/security" element={<Protected><Security /></Protected>} />
              <Route path="/blocked" element={<Protected><Blocked /></Protected>} />
              <Route path="/settings" element={<Protected><DisplaySettings /></Protected>} />
              <Route path="/accessibility" element={<AccessibilityStatement />} />
              <Route path="/settings/accessibility" element={<Protected><AccessibilityPage /></Protected>} />
              <Route path="/ccpa-opt-out" element={<CcpaOptOut />} />
              <Route path="/compliance" element={<CompliancePage />} />
              <Route path="/safety" element={<Protected><SafetyAlert /></Protected>} />
              <Route path="/w/:username" element={<Protected><Profile /></Protected>} />
              <Route path="/admin" element={<Protected><Admin /></Protected>} />
              <Route path="/mystic" element={<Protected><MysticProfile /></Protected>} />
              <Route path="*" element={<Protected><NotFound /></Protected>} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster theme="dark" richColors position="top-center" mobileOffset={{ top: "calc(env(safe-area-inset-top) + 16px)" }} offset={{ top: "calc(env(safe-area-inset-top) + 24px)" }} toastOptions={{ style: { background: "#131B26", border: "1px solid #1e293b", color: "#e2e8f0" } }} />
      </AuthProvider>
    </div>
  );
}

export default App;
